import { useState } from "react";
import { Check, Copy, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { useMe } from "@/api/hooks/useAuth";
import {
  useMojKluc,
  useVygenerujKluc,
  useZrusKluc,
} from "@/api/hooks/useMojKluc";
import { formatDateTime } from "@/lib/format";

/**
 * Môj kľúč pre Tandem Zostavy (KOLO 128).
 *
 * Program pri TopSolide posiela kusovník s týmto kľúčom a import je potom
 * **môj** — rozrobený import vidím len ja. Do KOLA 128 bol kľúč spoločný
 * (v `.env` na serveri) a kolega po prihlásení videl v pruhu cudzí import.
 *
 * Celý kľúč sa ukáže **raz**, hneď po vygenerovaní; server ukladá len
 * jeho odtlačok. Nový kľúč starý okamžite zneplatní — program so starým
 * dostane 401.
 *
 * Pri vypnutom prihlasovaní sa sekcia neukazuje: niet komu kľúč priradiť.
 */
export function ZostavyKlucSection(): JSX.Element | null {
  const me = useMe();
  const prihlaseny = !!me.data?.data?.user;
  const stav = useMojKluc(prihlaseny);
  const vygeneruj = useVygenerujKluc();
  const zrus = useZrusKluc();
  const [novy, setNovy] = useState<string | null>(null);
  const [skopirovane, setSkopirovane] = useState(false);

  if (!prihlaseny) return null;
  const k = stav.data?.data;

  async function handleVygeneruj(): Promise<void> {
    if (
      k?.existuje &&
      !window.confirm(
        "Vygenerovať nový kľúč? Starý prestane platiť hneď — Tandem Zostavy " +
          "s ním už nič nepošlú, kým v programe nevložíš nový.",
      )
    )
      return;
    try {
      const r = await vygeneruj.mutateAsync();
      setNovy(r.data.kluc);
      setSkopirovane(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Kľúč sa nepodarilo vygenerovať",
        description: extractErrorMessage(err),
      });
    }
  }

  async function handleZrus(): Promise<void> {
    if (
      !window.confirm(
        "Zrušiť kľúč? Tandem Zostavy s ním už nič nepošlú.",
      )
    )
      return;
    try {
      await zrus.mutateAsync();
      setNovy(null);
      toast({ title: "Kľúč zrušený" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Kľúč sa nepodarilo zrušiť",
        description: extractErrorMessage(err),
      });
    }
  }

  async function kopiruj(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      setSkopirovane(true);
    } catch {
      // Schránka nemusí byť dostupná (http, práva) — kľúč sa dá označiť ručne
      toast({ title: "Označ kľúč a skopíruj ho ručne (Ctrl+C)." });
    }
  }

  return (
    <section
      className="space-y-3 rounded-lg border bg-card p-4"
      aria-labelledby="zostavy-kluc-nadpis"
    >
      <div>
        <h2 id="zostavy-kluc-nadpis" className="flex items-center gap-2 text-xl">
          <KeyRound className="h-5 w-5 text-primary" />
          Môj kľúč pre Tandem Zostavy
        </h2>
        <p className="text-xs text-muted-foreground">
          Program pri TopSolide ním posiela kusovníky pod tvojím menom —
          rozrobené importy z neho vidíš len ty. Vlož ho v programe do poľa{" "}
          <strong>Kľúč</strong> a daj <strong>Uložiť nastavenie</strong>.
        </p>
      </div>

      {novy ? (
        <div
          className="space-y-2 rounded-md border border-primary/40 bg-tandem-red-light p-3"
          data-testid="novy-kluc"
        >
          <p className="text-sm font-medium">
            Toto je tvoj nový kľúč. Skopíruj si ho teraz — znova sa už
            neukáže.
          </p>
          <div className="flex gap-2">
            <Input
              readOnly
              value={novy}
              aria-label="Nový kľúč"
              className="font-mono text-xs"
              onFocus={(e) => e.currentTarget.select()}
            />
            <Button size="sm" variant="outline" onClick={() => void kopiruj(novy)}>
              {skopirovane ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              {skopirovane ? "Skopírované" : "Kopírovať"}
            </Button>
          </div>
        </div>
      ) : null}

      {k?.existuje ? (
        <div className="text-sm">
          <div>
            Platný kľúč <span className="font-mono">{k.prefix}…</span>, vytvorený{" "}
            {formatDateTime(k.created_at)}.
          </div>
          <div className="text-xs text-muted-foreground">
            {k.last_used_at
              ? `Program ho naposledy použil ${formatDateTime(k.last_used_at)}.`
              : "Program ho zatiaľ nepoužil."}
          </div>
        </div>
      ) : stav.isSuccess ? (
        <p className="text-sm text-muted-foreground">Zatiaľ nemáš žiadny kľúč.</p>
      ) : null}

      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={() => void handleVygeneruj()}
          disabled={vygeneruj.isPending}
        >
          {k?.existuje ? "Vygenerovať nový" : "Vygenerovať kľúč"}
        </Button>
        {k?.existuje ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => void handleZrus()}
            disabled={zrus.isPending}
          >
            Zrušiť kľúč
          </Button>
        ) : null}
      </div>
    </section>
  );
}
